import "./App.css";
import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { AppRoutes } from "./AppRoutes";
import { useJwt } from "react-jwt";
import { Box, CircularProgress, useMediaQuery } from "@mui/material";
import { core } from "./Core";
import { arrayEmpty, objectEmpty } from "./utils/Helpers";
import { setLocale } from "yup";
import { abm } from "./services/abm";

setLocale({
  mixed: {
    default: '${label}: Inválido',
    required: '${label}: Es un dato requerido',
    notType: '${label}: El dato no es válido',
  },
  string:{
    min: '${label}: El texto debe contener al menos ${min} caracteres',
    max: '${label}: El texto excedió el límite de ${max} caracteres'
  },
  number: {
    min: '${label}: El número debe ser mayor o igual que ${min}',
    max: '${label}: El número debe ser menor o igual que ${max}'
  },
  array: {
    min: '${label}: La lista debe tener al menos ${min} items',
    max: '${label}:La lista debe como máximo ${max} items',
  }
});

const defAppContext = ({tokenState, tokensState}) => {
  const [token, setToken] = tokenState || [[], () => {}];
  const [tokens, setTokens] = tokensState || [[], () => {}];

  const load = () => {
    setToken(localStorage.getItem("auth"));
    loadSings();
  }

  const singOut = () => {
    setToken(null);
    localStorage.removeItem("auth");
  }

  const singIn = ({token}, remember = false) => {
    if(remember){
      addSing(token);
    }
    setToken(token);
    localStorage.setItem("auth", token);
  }
  
  const addSing = (token) => {
    const tokensItems = localStorage.getItem("tokens") || "[]";
    const currentTokens = JSON.parse(tokensItems);
    const newSign = jwtToItem(token);

    const newTokens = [newSign, ...currentTokens.filter(x => x.id !== newSign.id)];
    setTokens(newTokens);
    localStorage.setItem("tokens", JSON.stringify(newTokens));
  }

  const removeSing = (sign) => {
    const tokensItems = localStorage.getItem("tokens") || "[]";
    const currentTokens = JSON.parse(tokensItems);
    const newTokens = currentTokens.filter(x => x.id !== sign.id);
    setTokens(newTokens);
    localStorage.setItem("tokens", JSON.stringify(newTokens));
  }

  const readSings = () => {
    return tokens;
  }

  const loadSings = () => {
    const tokensItems = localStorage.getItem("tokens") || "[]";
    const currentTokens = JSON.parse(tokensItems);
    setTokens(currentTokens);
  }

  function jwtToItem(token) {
    try {
      const payloadBase64 = token.split('.')[1];
      const decodedPayload = atob(payloadBase64);
      const parsed = JSON.parse(decodedPayload);
      return {...parsed, token};
    } catch (e) {
      console.error('JWT inválido:', e);
      return null;
    }
  }

  return {auth: token, isAuth: token, singOut, singIn, load, readSings, removeSing};
}

const AppContext = createContext(defAppContext({tokensState: ["", (data = "") => {}], tokensItems: [[], (data = []) => {}]}));
export const useAppContext = () => useContext(AppContext);

const App = () => { 
  //console.log("render app")
  const [writeFetching, setWriteFetching] = useState(false);
  const [fetching, setFetching] = useState(false);
  const context = defAppContext({tokenState: useState(null), tokensState: useState([])});
  const { isExpired, decodedToken } = useJwt(context.auth);
  const smMode = !useMediaQuery((theme) => theme.breakpoints.up('sm'));

  const claims = useMemo(()=> ({...decodedToken, centros: decodedToken?.centros?.split(",").map(c => parseInt(c))}), [decodedToken]);
  const appCore = useMemo(() => core({claims}), [claims]);

  const [globalQuery, setGlobalQuery] = useState(objectEmpty);
  const [authLoaded, setAuthLoaded] = useState(false);
  const [initialized, setInitialized] = useState(false);

  const setPropGlobalQuery = (prop, value) => {
    setGlobalQuery(current => ({...current, [prop]: value}));
  }



  useEffect(()=>{
    context.load();
    setAuthLoaded(true);
  }, []);

  useEffect(() => {
    if(!authLoaded) return;

    if(!context.auth){
      setInitialized(true);
      return;
    }

    setInitialized(false);
    const periodosService = abm(
      "periodos",
      {onError: (reason) => console.warn(reason)},
      context.auth,
      setWriteFetching,
      setFetching
    );

    periodosService.get("periodos/ultimo")
      .then(data => {
        if(data?.periodo?.id){
          setGlobalQuery(current => ({...current, id_periodo: data.periodo.id}));
        }
      })
      .catch(reason => {
        console.warn("No se pudo obtener el último período", reason);
      })
      .finally(() => {
        setInitialized(true);
      });
  }, [authLoaded, context.auth]);
  
  return (
    <AppContext.Provider value={{...context, setWriteFetching, writeFetching, setFetching, fetching, smMode, isExpired, claims, core: appCore, globalQuery, setPropGlobalQuery}}>
      {initialized
        ? <AppRoutes />
        : (
          <Box sx={{display: "flex", alignItems: "center", justifyContent: "center", height: "100vh"}}>
            <CircularProgress />
          </Box>
        )
      }
    </AppContext.Provider>
  );
}

export default App;